import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getSpaPackageSeries, saveSpaPackageSeries, checkSpaPackageSeriesBillingUsage, exportSpaPackageSeriesToExcel, exportSpaPackageSeriesDetailsToExcel } from "../../components/Api/GmsSpaPackageSeries";
import { getSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getProducts } from "../../components/Api/GmsProduct";
import { getTaxes } from "../../components/Api/GmsTax";
import { Eye, SquarePen, Plus, Minus, Filter, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  SeriesId: 0,
  SeriesCode: "",
  SeriesName_En: "",
  SeriesName_Ar: "",
  Amount: 0,
  Cost: 0,
  DiscountAmount: 0,
  DiscountPercentage: 0,
  TaxAmount: 0,
  NetAmount: 0,
  PackageCount: 0,
  Status: true,
  CreatedBy: 1,
  ModifiedBy: 1,
  Details: [],
  TaxIds: [],
};

const emptyFilter = {
  SeriesId: null,
  SeriesCode: "",
  SeriesName_En: "",
  Status: null,
};

const defaultPackageDetail = {
  ItemType: 'Package', // 'Package', 'Service', or 'Product'
  Package_Id: null,
  Service_Id: null,
  Product_Id: null,
  Quantity: 1,
};

export default function SpaPackageSeriesPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaPackageSeries');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaPackageSeries');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSpaPackageSeries');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsSpaPackageSeries');
  const isAdmin = isUserAdmin();

  const [spaPackageSeries, setSpaPackageSeries] = useState([]);
  const [spaPackages, setSpaPackages] = useState([]);
  const [spaServices, setSpaServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    return { ...defaultForm, Status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [packageDetails, setPackageDetails] = useState([defaultPackageDetail]);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isUsedInBilling, setIsUsedInBilling] = useState(false);
  const [originalPackageDetails, setOriginalPackageDetails] = useState([]);
  const [originalSeriesCode, setOriginalSeriesCode] = useState("");
  const [originalSeriesName_En, setOriginalSeriesName_En] = useState("");
  const [originalSeriesName_Ar, setOriginalSeriesName_Ar] = useState("");
  const [billingUsageMap, setBillingUsageMap] = useState({});
  const [installmentCount, setInstallmentCount] = useState(0);
  const [showInstallmentInput, setShowInstallmentInput] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [formKey, setFormKey] = useState(0);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadSpaPackages();
      loadSpaServices();
      loadProducts();
      loadTaxes();
      loadSpaPackageSeries();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadSpaPackageSeries();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  useEffect(() => {
    calculateDiscountAmount();
  }, [form.Amount, form.DiscountPercentage]);

  useEffect(() => {
    calculateTaxAmount();
  }, [form.Amount, form.DiscountAmount, selectedTaxes]);

  useEffect(() => {
    // Regenerate installments when Amount changes
    if (installmentCount > 0 && form.Amount > 0) {
      generateInstallments(installmentCount, form.Amount);
    }
  }, [form.Amount]);

  const loadSpaPackages = async () => {
    try {
      const data = await getSpaPackages({ Status: true });
      setSpaPackages(data || []);
    } catch (error) {
      console.error("Error loading spa packages:", error);
      Swal.fire("Error", "Failed to load spa packages", "error");
    }
  };

  const loadSpaServices = async () => {
    try {
      const data = await getSpaServices({ Status: true });
      setSpaServices(data || []);
    } catch (error) {
      console.error("Error loading spa services:", error);
      Swal.fire("Error", "Failed to load spa services", "error");
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({ Product_Status: true });
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
      Swal.fire("Error", "Failed to load products", "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  const loadSpaPackageSeries = async () => {
    try {
      setLoading(true);
      const data = await getSpaPackageSeries(filter);
      
      const processedData = (data || []).map(item => {
        const amount = parseFloat(item.amount) || 0;
        const discountAmount = parseFloat(item.discountAmount) || 0;
        const taxAmount = parseFloat(item.taxAmount) || 0;
        const netAmount = parseFloat(item.netAmount) || 0;
        
        // Validate and fix dates
        let createdOn = item.createdOn;
        let modifiedOn = item.modifiedOn;
        
        // Check if dates are valid, if not set to current time
        if (createdOn && isNaN(new Date(createdOn).getTime())) {
          createdOn = new Date().toISOString();
        }
        if (modifiedOn && isNaN(new Date(modifiedOn).getTime())) {
          modifiedOn = new Date().toISOString();
        }
        
        return {
          ...item,
          SeriesId: item.seriesId,
          SeriesCode: item.seriesCode,
          SeriesName_En: item.seriesName_En,
          SeriesName_Ar: item.seriesName_Ar,
          Amount: amount,
          Cost: parseFloat(item.cost) || 0,
          DiscountAmount: discountAmount,
          DiscountPercentage: parseFloat(item.discountPercentage) || 0,
          TaxAmount: taxAmount,
          NetAmount: netAmount,
          PackageCount: item.packageCount,
          Status: item.status,
          CreatedOn: createdOn,
          CreatedBy: item.createdBy,
          ModifiedOn: modifiedOn,
          ModifiedBy: item.modifiedBy,
          Details: item.details,
          TaxIds: item.taxIds,
          Installments: item.installments || [],
        };
      });
      
      setSpaPackageSeries(processedData);
      setTotalCount(processedData.length);
      
      // Check billing usage for all series
      console.log("Checking billing usage for all series...");
      for (const series of processedData) {
        try {
          await checkBillingUsageForSeries(series.SeriesId);
        } catch (error) {
          console.error(`Error checking billing usage for series ${series.SeriesId}:`, error);
        }
      }
    } catch (error) {
      console.error("Error loading spa package series:", error);
      Swal.fire("Error", "Failed to load spa package series", "error");
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscountAmount = () => {
    const amount = parseFloat(form.Amount) || 0;
    const discountPercentage = parseFloat(form.DiscountPercentage) || 0;
    const discountAmount = (amount * discountPercentage) / 100;
    
    setForm(prev => ({
      ...prev,
      DiscountAmount: discountAmount,
    }));
  };

  const calculateTaxAmount = () => {
    const amountAfterDiscount = (parseFloat(form.Amount) || 0) - (parseFloat(form.DiscountAmount) || 0);
    let totalTaxPercentage = 0;
    
    selectedTaxes.forEach(selectedTax => {
      // Find the original tax object to get the tax percentage
      const originalTax = taxes.find(tax => tax.tax_Id === selectedTax.value);
      if (originalTax) {
        totalTaxPercentage += parseFloat(originalTax.tax_Perc) || 0;
      }
    });
    
    const taxAmount = (amountAfterDiscount * totalTaxPercentage) / 100;
    const netAmount = amountAfterDiscount + taxAmount;
    
    setForm(prev => ({
      ...prev,
      TaxAmount: taxAmount,
      NetAmount: netAmount,
    }));
  };

  const generateInstallments = (count, amount) => {
    if (count <= 0 || amount <= 0) {
      setInstallments([]);
      return;
    }

    const installmentAmount = Math.round((amount / count) * 100) / 100; // Round to 2 decimal places
    const generatedInstallments = [];

    for (let i = 1; i <= count; i++) {
      generatedInstallments.push({
        InstallmentName: `Installment ${i}`,
        InstallmentAmount: installmentAmount,
        installmentName: `Installment ${i}`,
        installmentAmount: installmentAmount
      });
    }

    setInstallments(generatedInstallments);
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePackageDetailChange = (index, field, value) => {
    const updatedDetails = [...packageDetails];
    
    // If changing item type, clear all item IDs
    if (field === 'ItemType') {
      updatedDetails[index] = {
        ...defaultPackageDetail,
        ItemType: value,
        Quantity: updatedDetails[index].Quantity || 1,
      };
    } else {
      updatedDetails[index] = {
        ...updatedDetails[index],
        [field]: value,
      };
    }
    
    setPackageDetails(updatedDetails);
  };

  const addPackageDetail = () => {
    setPackageDetails([...packageDetails, { ...defaultPackageDetail }]);
  };

  const removePackageDetail = (index) => {
    if (packageDetails.length > 1) {
      const updatedDetails = packageDetails.filter((_, i) => i !== index);
      setPackageDetails(updatedDetails);
    }
  };

  const handleTaxChange = (selectedOptions) => {
    setSelectedTaxes(selectedOptions || []);
    setForm(prev => ({
      ...prev,
      TaxIds: selectedOptions ? selectedOptions.map(tax => tax.value) : [],
    }));
  };

  const handleInstallmentAmountChange = (index, value) => {
    const updatedInstallments = [...installments];
    updatedInstallments[index] = {
      ...updatedInstallments[index],
      InstallmentAmount: parseFloat(value) || 0,
      installmentAmount: parseFloat(value) || 0
    };
    setInstallments(updatedInstallments);
  };

  const removeInstallment = (index) => {
    const updatedInstallments = installments.filter((_, i) => i !== index);
    setInstallments(updatedInstallments);
    setInstallmentCount(updatedInstallments.length);
  };

  const validateInstallments = () => {
    if (installments.length === 0) return true;
    
    const totalInstallments = installments.reduce((sum, installment) => {
      return sum + (parseFloat(installment.InstallmentAmount || installment.installmentAmount) || 0);
    }, 0);
    
    const amount = parseFloat(form.Amount) || 0;
    return Math.abs(totalInstallments - amount) < 0.01; // Allow for small rounding differences
  };

  const handleClear = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    setForm({ ...defaultForm, Status: defaultStatus });
    setPackageDetails([defaultPackageDetail]);
    setSelectedTaxes([]);
    setIsEditing(false);
    setIsUsedInBilling(false);
    setOriginalPackageDetails([]);
    setOriginalSeriesCode("");
    setOriginalSeriesName_En("");
    setOriginalSeriesName_Ar("");
    setBillingUsageMap({});
    setInstallmentCount(0);
    setShowInstallmentInput(false);
    setInstallments([]);
    // Force re-render of Select components
    setFormKey(prev => prev + 1);
  };

  const handleSave = async () => {
    try {
      if (!form.SeriesCode.trim()) {
        Swal.fire("Error", "Please enter Series Code", "error");
        return;
      }
      if (!form.SeriesName_En.trim()) {
        Swal.fire("Error", "Please enter Series Name (English)", "error");
        return;
      }
      if (form.Cost <= 0) {
        Swal.fire("Error", "Please enter a valid Cost Price", "error");
        return;
      }
      if (form.Amount <= 0) {
        Swal.fire("Error", "Please enter a valid Sales Price", "error");
        return;
      }
      // Validate that each detail has exactly one item selected
      const invalidDetails = packageDetails.filter(detail => {
        const hasPackage = detail.Package_Id !== null && detail.Package_Id !== undefined;
        const hasService = detail.Service_Id !== null && detail.Service_Id !== undefined;
        const hasProduct = detail.Product_Id !== null && detail.Product_Id !== undefined;
        const itemCount = (hasPackage ? 1 : 0) + (hasService ? 1 : 0) + (hasProduct ? 1 : 0);
        return itemCount !== 1;
      });

      if (invalidDetails.length > 0) {
        Swal.fire("Error", "Please select a Package, Service, or Product for all items", "error");
        return;
      }

      // Validate quantities
      const invalidQuantities = packageDetails.filter(detail => 
        !detail.Quantity || detail.Quantity <= 0
      );
      if (invalidQuantities.length > 0) {
        Swal.fire("Error", "Please enter a valid quantity (greater than 0) for all items", "error");
        return;
      }
      if (installments.length > 0 && !validateInstallments()) {
        Swal.fire("Error", "Total of all installments must equal the Amount", "error");
        return;
      }

      // Check if items were changed when used in billing
      if (isUsedInBilling && isEditing) {
        const itemsChanged = JSON.stringify(packageDetails) !== JSON.stringify(originalPackageDetails);
        if (itemsChanged) {
          Swal.fire("Error", "Cannot modify item selection (packages, services, products) for series that has been used in billing. Only amount, discount, and tax can be changed.", "error");
          return;
        }
      }

      // Check if series code or names were changed when used in billing
      if (isUsedInBilling && isEditing) {
        const seriesCodeChanged = form.SeriesCode !== originalSeriesCode;
        const seriesNameEnChanged = form.SeriesName_En !== originalSeriesName_En;
        const seriesNameArChanged = form.SeriesName_Ar !== originalSeriesName_Ar;
        
        if (seriesCodeChanged || seriesNameEnChanged || seriesNameArChanged) {
          Swal.fire("Error", "Cannot modify Series Code or Series Names for series that has been used in billing. Only amount, discount, and tax can be changed.", "error");
          return;
        }
      }

      setSaveLoading(true);
      const session = getUserSession();
      
      // Prepare save data with proper date handling
      const now = new Date().toISOString();
      const saveData = {
        ...form,
        CreatedBy: isEditing ? form.CreatedBy : session.userId,
        ModifiedBy: session.userId,
        // Ensure valid dates - use existing CreatedOn for edits, current time for new records
        CreatedOn: isEditing && form.CreatedOn ? form.CreatedOn : now,
        ModifiedOn: now,
        Details: packageDetails.map(detail => ({
          Package_Id: detail.Package_Id || null,
          Service_Id: detail.Service_Id || null,
          Product_Id: detail.Product_Id || null,
          Quantity: detail.Quantity,
        })),
        TaxIds: selectedTaxes.map(tax => tax.value),
        Installments: installments,
      };
      
      // Validate dates before sending
      if (saveData.CreatedOn && isNaN(new Date(saveData.CreatedOn).getTime())) {
        saveData.CreatedOn = now;
      }
      if (saveData.ModifiedOn && isNaN(new Date(saveData.ModifiedOn).getTime())) {
        saveData.ModifiedOn = now;
      }
      
      console.log("Final save data:", saveData);
      console.log("Date validation - CreatedOn:", saveData.CreatedOn, "ModifiedOn:", saveData.ModifiedOn);
      console.log("Date validation - CreatedOn valid:", !isNaN(new Date(saveData.CreatedOn).getTime()));
      console.log("Date validation - ModifiedOn valid:", !isNaN(new Date(saveData.ModifiedOn).getTime()));

      const result = await saveSpaPackageSeries(saveData);
      Swal.fire("Success", "Spa Package Series saved successfully", "success");
      handleClear();
      loadSpaPackageSeries();
    } catch (error) {
      console.error("Error saving spa package series:", error);
      const errorMessage = error.response?.data?.message || "Failed to save spa package series";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const checkBillingUsageForSeries = async (seriesId) => {
    try {
      console.log(`Checking billing usage for series ${seriesId}...`);
      const billingUsageCheck = await checkSpaPackageSeriesBillingUsage(seriesId);
      console.log(`Billing usage result for series ${seriesId}:`, billingUsageCheck);
      
      setBillingUsageMap(prev => {
        const newMap = {
          ...prev,
          [seriesId]: billingUsageCheck.isUsedInBilling
        };
        console.log("Updated billing usage map:", newMap);
        return newMap;
      });
      
      return billingUsageCheck.isUsedInBilling;
    } catch (error) {
      console.error(`Error checking billing usage for series ${seriesId}:`, error);
      return false;
    }
  };

  const handleEdit = async (series) => {
    try {
      setEditLoadingRow(series.SeriesId);
      
      // Check if series is used in billing
      const isUsed = await checkBillingUsageForSeries(series.SeriesId);
      setIsUsedInBilling(isUsed);
      
      if (isUsed) {
        Swal.fire({
          title: "Warning",
          text: "This package series has been used in billing. Only amount, discount, and tax can be modified. Item selection (packages, services, products) cannot be changed.",
          icon: "warning",
          confirmButtonText: "Continue"
        });
      }
      
      setForm({
        SeriesId: series.SeriesId,
        SeriesCode: series.SeriesCode,
        SeriesName_En: series.SeriesName_En,
        SeriesName_Ar: series.SeriesName_Ar,
        Amount: series.Amount,
        Cost: series.Cost,
        DiscountAmount: series.DiscountAmount,
        DiscountPercentage: series.DiscountPercentage,
        TaxAmount: series.TaxAmount,
        NetAmount: series.NetAmount,
        PackageCount: series.PackageCount,
        Status: series.Status,
        CreatedBy: series.CreatedBy,
        CreatedOn: series.CreatedOn,
        ModifiedBy: series.ModifiedBy,
        ModifiedOn: series.ModifiedOn,
        Details: series.Details || [],
        TaxIds: series.TaxIds || [],
      });

      if (series.Details && series.Details.length > 0) {
        console.log("Series details:", series.Details);
        const mappedDetails = series.Details.map(detail => {
          const packageId = detail.package_Id || detail.Package_Id;
          const serviceId = detail.service_Id || detail.Service_Id;
          const productId = detail.product_Id || detail.Product_Id;
          
          // Determine item type
          let itemType = 'Package';
          if (serviceId) itemType = 'Service';
          else if (productId) itemType = 'Product';
          
          return {
            ItemType: itemType,
            Package_Id: packageId || null,
            Service_Id: serviceId || null,
            Product_Id: productId || null,
            Quantity: detail.quantity || detail.Quantity || 1,
          };
        });
        console.log("Mapped package details:", mappedDetails);
        setPackageDetails(mappedDetails);
        setOriginalPackageDetails([...mappedDetails]); // Store original for comparison
      } else {
        setPackageDetails([defaultPackageDetail]);
        setOriginalPackageDetails([defaultPackageDetail]);
      }

      // Store original series code and names for validation
      setOriginalSeriesCode(series.SeriesCode);
      setOriginalSeriesName_En(series.SeriesName_En);
      setOriginalSeriesName_Ar(series.SeriesName_Ar);

      if (series.TaxIds && series.TaxIds.length > 0) {
        const selectedTaxOptions = taxOptions.filter(option => 
          series.TaxIds.includes(option.value)
        );
        setSelectedTaxes(selectedTaxOptions);
      } else {
        setSelectedTaxes([]);
      }

      // Load existing installments if any
      if (series.Installments && series.Installments.length > 0) {
        setInstallments(series.Installments);
        setInstallmentCount(series.Installments.length);
        setShowInstallmentInput(true);
      } else {
        setInstallments([]);
        setInstallmentCount(0);
        setShowInstallmentInput(false);
      }

      setIsEditing(true);
      setActiveTab("1");
    } catch (error) {
      console.error("Error loading series for edit:", error);
      Swal.fire("Error", "Failed to load series for editing", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleView = async (series) => {
    try {
      const details = series.Details || [];
      const taxIds = series.TaxIds || [];
      
      // Get item names for display (packages, services, products)
      const itemNames = details?.map(detail => {
        const packageId = detail.package_Id || detail.Package_Id;
        const serviceId = detail.service_Id || detail.Service_Id;
        const productId = detail.product_Id || detail.Product_Id;
        const quantity = detail.quantity || detail.Quantity || 1;
        
        if (packageId) {
          const pkg = spaPackages.find(p => (p.PackageId || p.packageId) === packageId);
          return pkg ? `Package: ${pkg.PackageName_En || pkg.packageName_En} (Qty: ${quantity})` : `Package ID: ${packageId} (Qty: ${quantity})`;
        } else if (serviceId) {
          const svc = spaServices.find(s => (s.spaService_Id || s.SpaService_Id) === serviceId);
          return svc ? `Service: ${svc.spaServiceName_En || svc.SpaServiceName_En || svc.spaService_NameEn} (Qty: ${quantity})` : `Service ID: ${serviceId} (Qty: ${quantity})`;
        } else if (productId) {
          const prod = products.find(p => (p.Product_Id || p.product_Id) === productId);
          return prod ? `Product: ${prod.Product_NameEn || prod.product_NameEn} (Qty: ${quantity})` : `Product ID: ${productId} (Qty: ${quantity})`;
        }
        return 'Unknown Item';
      }).join(', ') || 'No items';

      // Get tax names for display
      const taxNames = taxIds?.map(taxId => {
        const tax = taxes.find(t => t.tax_Id === taxId);
        return tax ? `${tax.tax_NameEn} (${tax.tax_Perc}%)` : `Tax ID: ${taxId}`;
      }).join(', ') || 'No taxes';

      Swal.fire({
        title: `<div style="text-align: center; color: #556ee6; font-size: 24px; font-weight: bold; margin-bottom: 10px;">
                  <i class="bx bx-package" style="margin-right: 10px;"></i>
                  Spa Package Series Details
                </div>`,
        html: `
          <div style="max-width: 700px; margin: 0 auto;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #556ee6;">
                <h6 style="color: #556ee6; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-info-circle" style="margin-right: 5px;"></i>
                  Basic Information
                </h6>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Series Code:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${series.SeriesCode}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Series Name (English):</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${series.SeriesName_En}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Series Name (Arabic):</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${series.SeriesName_Ar || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Status:</strong>
                  <span style="margin-left: 5px; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; ${series.Status ? 'background: #d4edda; color: #155724;' : 'background: #f8d7da; color: #721c24;'}">
                    ${series.Status ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #28a745;">
                <h6 style="color: #28a745; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-money" style="margin-right: 5px;"></i>
                  Financial Details
                </h6>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Sales Price:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${(series.Amount || 0).toFixed(2)}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Cost Price:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${(series.Cost || 0).toFixed(2)}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Discount Percentage:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${(series.DiscountPercentage || 0).toFixed(2)}%</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Applied Taxes:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${taxNames}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Installments:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${series.Installments && series.Installments.length > 0 ? `${series.Installments.length} installments of ${(series.Installments[0].InstallmentAmount || series.Installments[0].installmentAmount || 0).toFixed(2)} each` : 'No installments'}</span>
                </div>
              </div>
            </div>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107; margin-bottom: 20px;">
              <h6 style="color: #ffc107; margin-bottom: 10px; font-weight: bold;">
                <i class="bx bx-list-check" style="margin-right: 5px;"></i>
                Items Included (Packages, Services, Products)
              </h6>
              <div style="color: #6c757d; line-height: 1.6;">
                ${itemNames}
              </div>
            </div>
          </div>
        `,
        confirmButtonText: "Close",
        width: '800px',
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        },
        customClass: {
          confirmButton: 'btn btn-primary',
          popup: 'swal2-custom-popup'
        }
      });
    } catch (error) {
      console.error("Error viewing spa package series:", error);
      Swal.fire("Error", "Failed to view spa package series details", "error");
    }
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(true);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.SeriesId !== null && filter.SeriesId !== undefined && { SeriesId: filter.SeriesId }),
        ...(filter.SeriesCode && filter.SeriesCode.trim() !== '' && { SeriesCode: filter.SeriesCode }),
        ...(filter.SeriesName_En && filter.SeriesName_En.trim() !== '' && { SeriesName_En: filter.SeriesName_En }),
        ...(filter.Status !== null && filter.Status !== undefined && { Status: filter.Status })
      };
      
      await exportSpaPackageSeriesToExcel(cleanFilter);
      Swal.fire("Success", "Spa package series report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa package series:", error);
      Swal.fire("Error", "Failed to export spa package series report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportDetails = async (seriesId) => {
    try {
      setLoading(true);
      await exportSpaPackageSeriesDetailsToExcel(seriesId);
      Swal.fire("Success", "Spa package series details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa package series details:", error);
      Swal.fire("Error", "Failed to export spa package series details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = spaPackageSeries.slice(startIndex, endIndex);

  // Pagination component (same style as SpaPackagesPage)
  const PaginationComponent = () => {
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{" "}
          {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="pagination-wrapper">
          <Pagination
            className="ant-pagination"
            current={page}
            total={totalCount}
            pageSize={pageSize}
            onChange={handlePageChange}
            showTitle={false}
          />
        </div>
      </div>
    );
  };

  // Options for dropdowns
  const packageOptions = useMemo(() => {
    return spaPackages.map(pkg => ({
      value: pkg.PackageId || pkg.packageId,
      label: pkg.PackageName_En || pkg.packageName_En
    }));
  }, [spaPackages]);

  const serviceOptions = useMemo(() => {
    return spaServices.map(svc => ({
      value: svc.spaService_Id || svc.SpaService_Id,
      label: svc.spaServiceName_En || svc.SpaServiceName_En || svc.spaService_NameEn
    }));
  }, [spaServices]);

  const productOptions = useMemo(() => {
    return products.map(prod => ({
      value: prod.Product_Id || prod.product_Id,
      label: prod.Product_NameEn || prod.product_NameEn
    }));
  }, [products]);

  // Get available options for a specific dropdown (excluding already selected items of the same type)
  const getAvailableOptions = (currentIndex, itemType) => {
    let selectedIds = [];
    
    if (itemType === 'Package') {
      selectedIds = packageDetails
        .map((detail, index) => index !== currentIndex && detail.ItemType === 'Package' ? detail.Package_Id : null)
        .filter(id => id !== null);
      return packageOptions.filter(option => !selectedIds.includes(option.value));
    } else if (itemType === 'Service') {
      selectedIds = packageDetails
        .map((detail, index) => index !== currentIndex && detail.ItemType === 'Service' ? detail.Service_Id : null)
        .filter(id => id !== null);
      return serviceOptions.filter(option => !selectedIds.includes(option.value));
    } else if (itemType === 'Product') {
      selectedIds = packageDetails
        .map((detail, index) => index !== currentIndex && detail.ItemType === 'Product' ? detail.Product_Id : null)
        .filter(id => id !== null);
      return productOptions.filter(option => !selectedIds.includes(option.value));
    }
    
    return [];
  };

  const taxOptions = useMemo(() => {
    return taxes.map(tax => ({
      value: tax.tax_Id,
      label: `${tax.tax_NameEn} (${tax.tax_Perc}%)`
    }));
  }, [taxes]);

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" }
  ];

  // Get item name by ID and type
  const getItemName = (itemId, itemType) => {
    if (itemType === 'Package' || !itemType) {
      const pkg = spaPackages.find(p => (p.PackageId || p.packageId) === itemId);
      return pkg ? (pkg.PackageName_En || pkg.packageName_En) : "N/A";
    } else if (itemType === 'Service') {
      const svc = spaServices.find(s => (s.spaService_Id || s.SpaService_Id) === itemId);
      return svc ? (svc.spaServiceName_En || svc.SpaServiceName_En || svc.spaService_NameEn) : "N/A";
    } else if (itemType === 'Product') {
      const prod = products.find(p => (p.Product_Id || p.product_Id) === itemId);
      return prod ? (prod.Product_NameEn || prod.product_NameEn) : "N/A";
    }
    return "N/A";
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Spa Package Series Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Series</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Series List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        {isUsedInBilling && isEditing && (
                          <div className="alert alert-info mb-4" role="alert">
                            <i className="bx bx-info-circle me-2"></i>
                            <strong>Edit Mode:</strong> This package series has been used in billing. Only amount, discount percentage, and tax selection can be modified. Item selection (packages, services, products) cannot be changed.
                          </div>
                        )}
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="SeriesCode">Series Code <span className="text-danger">*</span></Label>
                              <Input
                                id="SeriesCode"
                                name="SeriesCode"
                                type="text"
                                value={form.SeriesCode}
                                onChange={(e) => setForm({ ...form, SeriesCode: e.target.value })}
                                placeholder="Enter Series Code"
                                required
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="SeriesName_En">Series Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                id="SeriesName_En"
                                name="SeriesName_En"
                                type="text"
                                value={form.SeriesName_En}
                                onChange={(e) => setForm({ ...form, SeriesName_En: e.target.value })}
                                placeholder="Enter Series Name in English"
                                required
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="SeriesName_Ar">Series Name (Arabic)</Label>
                              <Input
                                id="SeriesName_Ar"
                                name="SeriesName_Ar"
                                type="text"
                                value={form.SeriesName_Ar}
                                onChange={(e) => setForm({ ...form, SeriesName_Ar: e.target.value })}
                                placeholder="Enter Series Name in Arabic"
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Items Details Section (Packages, Services, Products) */}
                        <Row>
                          <Col md={12}>
                            {isUsedInBilling && (
                              <div className="alert alert-warning mb-3" role="alert">
                                <i className="bx bx-info-circle me-2"></i>
                                <strong>Warning:</strong> This package series has been used in billing. Item selection cannot be modified. Only amount, discount, and tax can be changed.
                              </div>
                            )}
                            <Label className="fw-bold mb-3">Items (Packages, Services, Products) <span className="text-danger">*</span></Label>
                            {packageDetails.map((detail, index) => (
                              <Row key={index} className="mb-3 align-items-end">
                                <Col md={3}>
                                  <FormGroup>
                                    <Label>Item Type</Label>
                                    <Select
                                      key={`itemtype-${index}-${formKey}`}
                                      value={{ value: detail.ItemType, label: detail.ItemType }}
                                      onChange={(selected) => handlePackageDetailChange(index, 'ItemType', selected?.value || 'Package')}
                                      options={[
                                        { value: 'Package', label: 'Package' },
                                        { value: 'Service', label: 'Service' },
                                        { value: 'Product', label: 'Product' }
                                      ]}
                                      placeholder="Select Type"
                                      styles={selectStyles}
                                      isSearchable={false}
                                      isDisabled={isUsedInBilling}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={5}>
                                  <FormGroup>
                                    <Label>{detail.ItemType || 'Item'}</Label>
                                    {detail.ItemType === 'Package' && (
                                      <Select
                                        key={`package-${index}-${formKey}`}
                                        value={packageOptions.find(option => option.value === detail.Package_Id)}
                                        onChange={(selected) => handlePackageDetailChange(index, 'Package_Id', selected?.value || null)}
                                        options={getAvailableOptions(index, 'Package')}
                                        placeholder="Select Package"
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        maxMenuHeight={200}
                                        isDisabled={isUsedInBilling}
                                      />
                                    )}
                                    {detail.ItemType === 'Service' && (
                                      <Select
                                        key={`service-${index}-${formKey}`}
                                        value={serviceOptions.find(option => option.value === detail.Service_Id)}
                                        onChange={(selected) => handlePackageDetailChange(index, 'Service_Id', selected?.value || null)}
                                        options={getAvailableOptions(index, 'Service')}
                                        placeholder="Select Service"
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        maxMenuHeight={200}
                                        isDisabled={isUsedInBilling}
                                      />
                                    )}
                                    {detail.ItemType === 'Product' && (
                                      <Select
                                        key={`product-${index}-${formKey}`}
                                        value={productOptions.find(option => option.value === detail.Product_Id)}
                                        onChange={(selected) => handlePackageDetailChange(index, 'Product_Id', selected?.value || null)}
                                        options={getAvailableOptions(index, 'Product')}
                                        placeholder="Select Product"
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        maxMenuHeight={200}
                                        isDisabled={isUsedInBilling}
                                      />
                                    )}
                                  </FormGroup>
                                </Col>
                                <Col md={2}>
                                  <FormGroup>
                                    <Label>Quantity</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={detail.Quantity || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || parseInt(value) >= 1) {
                                          handlePackageDetailChange(index, 'Quantity', value === '' ? '' : parseInt(value));
                                        }
                                      }}
                                      placeholder="Qty"
                                      disabled={isUsedInBilling}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={2}>
                                  <div className="d-flex gap-2">
                                    {packageDetails.length > 1 && (
                                      <Button
                                        type="button"
                                        color="danger"
                                        size="sm"
                                        onClick={() => removePackageDetail(index)}
                                        disabled={isUsedInBilling}
                                      >
                                        <Minus size={16} />
                                      </Button>
                                    )}
                                    {index === packageDetails.length - 1 && (
                                      <Button
                                        type="button"
                                        color="success"
                                        size="sm"
                                        onClick={addPackageDetail}
                                        disabled={isUsedInBilling}
                                      >
                                        <Plus size={16} />
                                      </Button>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            ))}
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Cost">Cost Price <span className="text-danger">*</span></Label>
                              <Input
                                id="Cost"
                                name="Cost"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.Cost}
                                onChange={(e) => {
                                  const cost = parseFloat(e.target.value) || 0;
                                  setForm({ ...form, Cost: cost });
                                }}
                                placeholder="Enter Cost Price"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Amount">Sales Price <span className="text-danger">*</span></Label>
                              <Input
                                id="Amount"
                                name="Amount"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.Amount}
                                onChange={(e) => {
                                  const amount = parseFloat(e.target.value) || 0;
                                  setForm({ ...form, Amount: amount });
                                }}
                                placeholder="Enter Sales Price"
                                required
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="DiscountPercentage">Discount Percentage (%)</Label>
                              <Input
                                id="DiscountPercentage"
                                name="DiscountPercentage"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={form.DiscountPercentage}
                                onChange={(e) => setForm({ ...form, DiscountPercentage: parseFloat(e.target.value) || 0 })}
                                placeholder="Enter Discount %"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="taxes">Taxes</Label>
                              <Select
                                key={`taxes-${formKey}`}
                                value={selectedTaxes}
                                onChange={handleTaxChange}
                                options={taxOptions}
                                placeholder="Select Taxes"
                                styles={selectStyles}
                                isMulti
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="NetAmount">Net Amount</Label>
                              <Input
                                id="NetAmount"
                                name="NetAmount"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.NetAmount}
                                readOnly
                                className="bg-light"
                                placeholder="Calculated automatically"
                              />
                            </FormGroup>
                          </Col>
                          {hasDeletePermission && (
                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label for="Status">Status</Label>
                                <div className="form-check form-switch mt-2">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="Status"
                                    checked={form.Status}
                                    onChange={(e) => setForm({ ...form, Status: e.target.checked })}
                                  />
                                  <label className="form-check-label" htmlFor="Status">
                                    {form.Status ? "Active" : "Inactive"}
                                  </label>
                                </div>
                              </FormGroup>
                            </Col>
                          )}
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <div className="d-flex align-items-end gap-2">
                                <div className="flex-grow-1">
                                  <Button
                                    type="button"
                                    color="info"
                                    onClick={() => setShowInstallmentInput(!showInstallmentInput)}
                                    className="waves-effect waves-light"
                                  >
                                    Set Installment
                                  </Button>
                                </div>
                                {showInstallmentInput && (
                                  <div className="flex-grow-1">
                                    <Input
                                      type="number"
                                      min="1"
                                      max="12"
                                      value={installmentCount}
                                      onChange={(e) => {
                                        const count = parseInt(e.target.value) || 0;
                                        setInstallmentCount(count);
                                        if (count > 0 && form.Amount > 0) {
                                          generateInstallments(count, form.Amount);
                                        }
                                      }}
                                      placeholder="Number of installments"
                                    />
                                  </div>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            {installments.length > 0 && (
                              <FormGroup className="mb-4">
                                <Label>Installments</Label>
                                <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                  {installments.map((installment, index) => {
                                    const installmentAmount = parseFloat(installment.InstallmentAmount || installment.installmentAmount) || 0;
                                    const discountAmount = (installmentAmount * form.DiscountPercentage) / 100;
                                    const amountAfterDiscount = installmentAmount - discountAmount;
                                    
                                    // Calculate tax on the amount after discount
                                    let totalTaxPercentage = 0;
                                    selectedTaxes.forEach(selectedTax => {
                                      const originalTax = taxes.find(tax => tax.tax_Id === selectedTax.value);
                                      if (originalTax) {
                                        totalTaxPercentage += parseFloat(originalTax.tax_Perc) || 0;
                                      }
                                    });
                                    const taxAmount = (amountAfterDiscount * totalTaxPercentage) / 100;
                                    const netAmount = amountAfterDiscount + taxAmount;
                                    
                                    return (
                                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="flex-grow-1">
                                          <small className="text-muted d-block">{installment.InstallmentName || installment.installmentName}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={installmentAmount}
                                            onChange={(e) => handleInstallmentAmountChange(index, e.target.value)}
                                            style={{ width: '100px', height: '30px', fontSize: '12px' }}
                                          />
                                          <small className="text-info" style={{ minWidth: '60px', fontSize: '11px' }}>
                                            Net: {netAmount.toFixed(2)}
                                          </small>
                                          <Button
                                            type="button"
                                            color="danger"
                                            size="sm"
                                            onClick={() => removeInstallment(index)}
                                            style={{ height: '30px', width: '30px', padding: '0' }}
                                          >
                                            <Minus size={12} />
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  <div className="mt-2 pt-2 border-top">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <small>Total Installments:</small>
                                      <small className="text-primary">
                                        {installments.reduce((sum, installment) => 
                                          sum + (parseFloat(installment.InstallmentAmount || installment.installmentAmount) || 0), 0
                                        ).toFixed(2)}
                                      </small>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <small>Total Net Amount:</small>
                                      <small className="text-success">
                                        {installments.reduce((sum, installment) => {
                                          const installmentAmount = parseFloat(installment.InstallmentAmount || installment.installmentAmount) || 0;
                                          const discountAmount = (installmentAmount * form.DiscountPercentage) / 100;
                                          const amountAfterDiscount = installmentAmount - discountAmount;
                                          
                                          let totalTaxPercentage = 0;
                                          selectedTaxes.forEach(selectedTax => {
                                            const originalTax = taxes.find(tax => tax.tax_Id === selectedTax.value);
                                            if (originalTax) {
                                              totalTaxPercentage += parseFloat(originalTax.tax_Perc) || 0;
                                            }
                                          });
                                          const taxAmount = (amountAfterDiscount * totalTaxPercentage) / 100;
                                          const netAmount = amountAfterDiscount + taxAmount;
                                          
                                          return sum + netAmount;
                                        }, 0).toFixed(2)}
                                      </small>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <small>Package Net Amount:</small>
                                      <small className="text-info">
                                        {(form.NetAmount || 0).toFixed(2)}
                                      </small>
                                    </div>
                                    {!validateInstallments() && (
                                      <small className="text-danger">
                                        Total installments must equal Amount
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </FormGroup>
                            )}
                          </Col>
                        </Row>

                        <Row className="mt-4">
                          <Col md={12}>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading || !form.SeriesName_En.trim() || !form.SeriesCode.trim()}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Series" : "Save Series"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Series</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={4}>
                                <Label for="filterSeriesName">Series Name</Label>
                                <Input
                                  id="filterSeriesName"
                                  name="filterSeriesName"
                                  type="text"
                                  value={filter.SeriesName_En}
                                  onChange={(e) => setFilter({ ...filter, SeriesName_En: e.target.value })}
                                  placeholder="Search series name..."
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterSeriesCode">Series Code</Label>
                                <Input
                                  id="filterSeriesCode"
                                  name="filterSeriesCode"
                                  type="text"
                                  value={filter.SeriesCode}
                                  onChange={(e) => setFilter({ ...filter, SeriesCode: e.target.value })}
                                  placeholder="Search series code..."
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterStatus">Status</Label>
                                <Select
                                  value={statusOptions.find(option => option.value === filter.Status)}
                                  onChange={(selected) => setFilter({ ...filter, Status: selected?.value || null })}
                                  options={statusOptions}
                                  placeholder="Select Status"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Spa Package Series List
                            </h5>
                            <div className="d-flex gap-2">
                              <Button
                                color="success"
                                onClick={handleExportExcel}
                                disabled={loading || totalCount === 0}
                                className="waves-effect waves-light"
                              >
                                <Download className="me-1" size={16} />
                                Export Excel
                              </Button>
                              {hasEditOrHigher && (
                                <Button
                                  color="primary"
                                  onClick={() => setActiveTab("1")}
                                  className="waves-effect waves-light"
                                >
                                  <i className="bx bx-plus me-1"></i>
                                  Add New Series
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading spa package series...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="10%">Series Code</th>
                                      <th width="15%">Series Name (En)</th>
                                      <th width="15%">Series Name (Ar)</th>
                                      <th width="8%">Discount %</th>
                                      <th width="10%">Sales Price</th>
                                      <th width="10%">Cost Price</th>
                                      <th width="8%">Status</th>
                                      <th width="7%">Billing</th>
                                      <th width="12%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={item.SeriesId}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{item.SeriesCode}</td>
                                          <td>{item.SeriesName_En}</td>
                                          <td>{(item.SeriesName_Ar) || 'N/A'}</td>
                                          <td className="text-end">{(item.DiscountPercentage || 0).toFixed(2)}%</td>
                                          <td className="text-end">{(item.Amount || 0).toFixed(2)}</td>
                                          <td className="text-end">{(item.Cost || 0).toFixed(2)}</td>
                                          <td>
                                            <span className={`badge ${item.Status ? 'bg-success' : 'bg-danger'}`}>
                                              {item.Status ? "Active" : "Inactive"}
                                            </span>
                                          </td>
                                                                                     <td>
                                             {(() => {
                                               const usage = billingUsageMap[item.SeriesId];
                                               console.log(`Series ${item.SeriesId} billing usage:`, usage);
                                               if (usage === true) {
                                                 return <span className="badge bg-warning text-dark">Used</span>;
                                               } else if (usage === false) {
                                                 return <span className="badge bg-success">Available</span>;
                                               } else {
                                                 return <span className="badge bg-secondary">Unknown</span>;
                                               }
                                             })()}
                                           </td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                                title="View"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => handleExportDetails(item.SeriesId)}
                                                disabled={loading}
                                                className="waves-effect waves-light"
                                                title="Export Details"
                                              >
                                                <Download size={12} />
                                              </Button>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === item.SeriesId}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === item.SeriesId ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="10" className="text-center">
                                          {hasAppliedFilter ? "No spa package series found matching the filter criteria." : "No spa package series available. Please add some series first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalCount > 0 && <PaginationComponent />}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
